import { useQuery, useMutation } from '@tanstack/react-query'
import { MutationConfig, QueryConfig, QueryConfigPickRes } from '../../reactQuery'
import {
  fetchDDOTSubmissions,
  fetchEnterpriseEin,
  fetchTDMStrategies,
  postSubmission,
  patchSubmission,
  Submission,
  TDMStrategyProps
} from './api'
import { Required } from 'utility-types'

export const useDDOTSubmissions = <T = Submission[]>(
  queryParams: Parameters<typeof fetchDDOTSubmissions>[0] = {},
  options?: QueryConfigPickRes<typeof fetchDDOTSubmissions, 'records', T>
) =>
  useQuery({
    queryKey: ['ddot-submissions', queryParams],
    queryFn: async () => {
      const { records } = await fetchDDOTSubmissions(queryParams)
      return records
    },
    ...options
  })

export function useEnterpriseEin<T>(
  submissionTaxId: string,
  queryParams: Parameters<typeof fetchEnterpriseEin>[1] = {},
  options?: QueryConfig<typeof fetchEnterpriseEin, T>
) {
  return useQuery({
    queryKey: ['ddot-submissions', 'ein', submissionTaxId, queryParams],
    queryFn: async () => (submissionTaxId.length ? fetchEnterpriseEin(submissionTaxId, queryParams) : null),
    ...options
  })
}

export function useTDMStrategies<T = TDMStrategyProps[]>(
  queryParams: Parameters<typeof fetchTDMStrategies>[0] = {},
  options?: QueryConfigPickRes<typeof fetchTDMStrategies, 'records', T>
) {
  return useQuery({
    queryKey: ['tdm-strategies', queryParams],
    queryFn: async () => {
      const { records } = await fetchTDMStrategies(queryParams)
      return records
    },
    ...options
  })
}

export const usePostSubmission = (options?: MutationConfig<typeof postSubmission>) =>
  useMutation({ mutationFn: (body) => postSubmission(body), ...options })

export const usePatchSubmission = (
  options?: MutationConfig<
    typeof patchSubmission,
    Required<Partial<Submission>, 'id'> & { submissionType: 'save' | 'submit' }
  >
) =>
  useMutation({
    mutationFn: ({ id, submissionType, ...body }) => patchSubmission(id, body, submissionType),
    ...options
  })
